import { useEffect, useMemo, useRef, useState } from 'react';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import Notice from '../../components/Notice.jsx';
import PageState from '../../components/PageState.jsx';
import RoleChips from '../../components/RoleChips.jsx';
import { api } from '../../lib/api.js';
import { ROLE_IDS, ROLE_LABELS, buildPeopleRows, isStaffEmail, manageable, peopleSearchText } from '../../lib/people-rows.js';
import { GUARDIAN_STATUS_LABEL } from '../../lib/watch-format.js';
import { OnboardingCard, accountLabel, auditorSelectableIn, fmt, viewerSelectableIn } from './people-format.jsx';
import { InviteDialog, RevokeDialog } from './PeopleDialogs.jsx';
import ManageRolesDialog from './ManageRolesDialog.jsx';

const TONE = { green: 'badge-green', amber: 'badge-amber', red: 'badge-red', slate: 'badge-slate' };
const ORIGINS = ['SSO', 'Invite', 'Directory'];
const NOTE = 'Last sign-in is not shown: the identity platform keeps no sign-in history.';

function Detail({ row, customerId, data, isOperator, onChanged }) {
  const m = row.member;
  const g = row.guardian;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.confirmWatchGuardianAddress(customerId, g.guardianId, g.pendingAddress);
      onChanged();
    } catch (e) {
      setError(e?.message || 'The address could not be confirmed.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-3 text-sm">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1">
        {m ? <><dt className="text-ipc-muted">Account</dt><dd>{m.status === 'pending-first-sign-in' && !m.account ? 'Not signed in yet' : accountLabel(m.account)}</dd></> : null}
        {m?.invitedAt ? <><dt className="text-ipc-muted">Invited</dt><dd>{fmt(m.invitedAt)}</dd></> : null}
        {m?.status === 'pending-first-sign-in' ? <><dt className="text-ipc-muted">Pending</dt><dd>Roles apply at their first IPC Global sign-in{m.expiresAt ? `, if before ${fmt(m.expiresAt)}` : ''}.</dd></> : null}
        {m?.status === 'revoked' && m.revokedAt ? <><dt className="text-ipc-muted">Revoked</dt><dd>{fmt(m.revokedAt)}</dd></> : null}
        {m?.isClaimant ? <><dt className="text-ipc-muted">Onboarding</dt><dd>Claimed onboarding</dd></> : null}
        {m?.isConsentAdmin ? <><dt className="text-ipc-muted">Consent</dt><dd>Granted consent</dd></> : null}
        {g ? <><dt className="text-ipc-muted">Guardian</dt><dd>{GUARDIAN_STATUS_LABEL[g.status] ?? g.status}{g.undeliverable ? ', address bounced' : ''}{g.unsubscribed ? ', unsubscribed' : ''}</dd></> : null}
        {g && g.lookup !== row.email ? <><dt className="text-ipc-muted">Typed as</dt><dd className="break-all">{g.lookup}</dd></> : null}
      </dl>
      {g?.pendingAddress ? (
        <Notice
          tone="warn"
          role="status"
          titleAs="p"
          title="New directory address"
          action={<button type="button" className="btn-secondary text-sm" disabled={busy} onClick={confirm}>Send to {g.pendingAddress}</button>}
        >
          The directory now shows a new address for this guardian. A directory address change can indicate an account takeover, so confirm only if you expected this change.
          {error ? <p role="alert" className="mt-1 text-red-700">{error}</p> : null}
        </Notice>
      ) : null}
      {isOperator && m?.isClaimant && data.onboarding ? <OnboardingCard onboarding={data.onboarding} /> : null}
    </div>
  );
}

export default function PeopleTab({
  customerId, data, guardians, canGuardian, isOperator, resendInvite = false,
  onInvite, onRevoke, onResend, onChanged, prefillEmail = '', onPrefillConsumed, inviteRoles = null, onInviteRolesConsumed,
}) {
  const [managing, setManaging] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [inviteFor, setInviteFor] = useState(null); // null closed, '' open and empty, otherwise the prefilled address
  const [invitePreset, setInvitePreset] = useState([]); // roles preselected when the dialog opened
  const [auditorNotice, setAuditorNotice] = useState(false);
  const openerRef = useRef(null);

  useEffect(() => {
    if (prefillEmail) {
      setInviteFor(prefillEmail);
      onPrefillConsumed?.();
    }
  }, [prefillEmail, onPrefillConsumed]);

  // A link from the Compliance Schedule tab (?invite=auditor): open the same invite dialog, Auditor preselected.
  const auditorOk = auditorSelectableIn(data);
  useEffect(() => {
    if (!inviteRoles) return;
    if (auditorOk) { setInvitePreset(inviteRoles); setInviteFor(''); } else setAuditorNotice(true);
    onInviteRolesConsumed?.();
  }, [inviteRoles, auditorOk, onInviteRolesConsumed]);

  const visibleGuardians = canGuardian ? guardians ?? [] : [];
  const rows = useMemo(() => buildPeopleRows({ members: data.members ?? [], guardians: visibleGuardians }), [data.members, guardians, canGuardian]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      id: 'person',
      header: 'Person',
      sortValue: (r) => r.email.toLowerCase(),
      cell: (r) => (
        <div className="min-w-0 break-words">
          {r.name ? <div className="font-medium text-ipc-ink">{r.name}</div> : null}
          <div className={r.name ? 'text-xs text-ipc-muted' : 'text-ipc-ink'}>{r.email}</div>
        </div>
      ),
    },
    { id: 'roles', header: 'Roles', cell: (r) => <RoleChips roles={r.roles} max={3} label={`Roles of ${r.email}`} /> },
    { id: 'origin', header: 'Origin', hideBelow: 'md', sortValue: (r) => r.origin, cell: (r) => r.origin },
    { id: 'status', header: 'Status', sortValue: (r) => r.status.label, cell: (r) => <span className={TONE[r.status.tone]}>{r.status.label}</span> },
  ];

  const filters = [
    {
      id: 'role',
      label: 'Role',
      options: ROLE_IDS.filter((id) => canGuardian || id !== 'guardian').map((id) => ({ value: id, label: ROLE_LABELS[id] })),
      test: (r, v) => r.roles.some((c) => c.id === v),
    },
    {
      id: 'origin',
      label: 'Origin',
      options: ORIGINS.filter((o) => canGuardian || o !== 'Directory').map((o) => ({ value: o, label: o })),
      test: (r, v) => r.origin === v,
    },
  ];

  function rowActions(row) {
    if (row.kind === 'directory') {
      return [
        { id: 'manage', label: 'Manage roles', onSelect: () => setManaging(row) },
        { id: 'invite', label: isStaffEmail(row.email) ? 'Pre-assign roles' : 'Invite to portal', onSelect: () => setInviteFor(row.email) },
      ];
    }
    const m = row.member;
    const can = manageable(m);
    const invited = m.account?.status === 'FORCE_CHANGE_PASSWORD';
    return [
      { id: 'manage', label: 'Manage roles', hidden: !can && !canGuardian, onSelect: () => setManaging(row) },
      { id: 'resend', label: 'Resend invite', hidden: !(invited && resendInvite && typeof onResend === 'function'), onSelect: () => onResend(m.email) },
      { id: 'revoke', label: 'Revoke', danger: true, hidden: !can, onSelect: () => setRevoking(row) },
    ];
  }

  const caption = data.truncated
    ? `Showing the first ${data.members.length} members.${isOperator ? ' The rest are listed in IdP Admin.' : ''} ${NOTE}`
    : NOTE;

  return (
    <div className="space-y-3">
      {auditorNotice ? <Notice tone="info" role="status" titleAs="p" title="Auditor access is not available yet. Contact support." /> : null}
      {data.membersError ? <PageState state="error" title="The member list is unavailable right now" message="Try again in a moment." onRetry={onChanged} /> : null}
      {data.tenantMissing && !data.membersError ? <Notice tone="info" role="status" titleAs="p" title="No one has signed in yet">Invite or pre-assign the first person. Their sign-in is created when you do.</Notice> : null}
      {!data.membersError ? (
        <ConsoleTable
          label="People"
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          rowName={(r) => r.email}
          searchText={peopleSearchText}
          searchPlaceholder="Search people"
          filters={filters}
          defaultSort={{ columnId: 'person', direction: 'ascending' }}
          renderExpanded={(r) => <Detail row={r} customerId={customerId} data={data} isOperator={isOperator} onChanged={onChanged} />}
          rowActions={rowActions}
          caption={caption}
          actions={<button ref={openerRef} type="button" className="btn-primary" onClick={() => { setInvitePreset([]); setInviteFor(''); }}>Invite or pre-assign</button>}
          emptyState={{ title: 'No people yet', body: 'Invite the first person.' }}
        />
      ) : null}
      {managing ? (
        <ManageRolesDialog
          key={managing.id}
          row={managing}
          customerId={customerId}
          canGuardian={canGuardian}
          auditorSelectable={auditorSelectableIn(data)}
          viewerSelectable={viewerSelectableIn(data)}
          tenantLocked={managing.kind === 'member' && !manageable(managing.member)}
          guardianCount={visibleGuardians.length}
          onClose={() => setManaging(null)}
          onDone={async () => { await onChanged(); }}
        />
      ) : null}
      {revoking ? <RevokeDialog row={revoking} onClose={() => setRevoking(null)} onConfirm={onRevoke} /> : null}
      {inviteFor !== null ? <InviteDialog auditorSelectable={auditorSelectableIn(data)} viewerSelectable={viewerSelectableIn(data)} canGuardian={canGuardian} guardianCount={visibleGuardians.length} prefillEmail={inviteFor} initialRoles={invitePreset} onClose={() => { setInviteFor(null); setInvitePreset([]); }} onInvite={onInvite} returnFocusRef={openerRef} /> : null}
    </div>
  );
}
